import { z } from 'zod';

/**
 * Posição do aparelho no momento em que a etiqueta foi impressa ou gravada na
 * tag NFC — os subobjetos `printLocation` e `nfcLocation` de
 * `tenants/{tenantId}/labels/{labelId}`, e o agregado
 * `tenants/{tenantId}/operating_locations/{geohash6}`.
 *
 * Origem: #996 — 2026-10-18 14:17 +0200 (owner: "ao imprimir ou gravar NFC
 * deve gravar a localização na etiqueta; sem acesso à localização não deve
 * poder gravar").
 *
 * Por quê: a equipe interna precisa saber onde cada tenant opera de fato. O
 * endereço do tenant (`tenant.address/latitude/longitude`) é o DECLARADO no
 * onboarding; a posição do aparelho na hora de imprimir é a observada. A aba
 * "Local" do tenant no admin mostra os dois lado a lado.
 *
 * Por que fica fora de `schemas.ts`: mesmo motivo do `labelNfc.ts` — o
 * `LabelSchema` importa este schema e o contrato do callable (`nfc.ts`) também;
 * um arquivo só fecharia ciclo de import com os branded ids.
 *
 * Contrato / Dados — exemplo anonimizado de `labels/{id}.printLocation`:
 *   {
 *     "lat": -23.5613,            // graus decimais, WGS84
 *     "lng": -46.6565,
 *     "accuracyM": 18.5,          // METROS, raio de ~68 % (Android `Location.accuracy`,
 *                                 // iOS `horizontalAccuracy`); aproximada chega a ~3000
 *     "fixedAtMillis": 1760789820000, // hora da POSIÇÃO dada pelo provedor (epoch-ms),
 *                                 // não a hora da impressão — num LAST_KNOWN pode ser
 *                                 // até 24 h antes
 *     "source": "FRESH",          // FRESH = obtida agora; LAST_KNOWN = cache do SO (≤ 24 h)
 *     "capturedVia": "PRINT",     // PRINT (cliente, markPrinted) | NFC (servidor, bindNfcTag)
 *     "platform": "ANDROID"       // ANDROID | IOS
 *   }
 * `printLocation` é escrito pelo CLIENTE a cada impressão (sobrescreve), nos
 * ramos de impressão das rules, que validam o formato. `nfcLocation` é escrito
 * SÓ pelo callable `bindNfcTag` (Admin SDK); as rules recusam o cliente.
 * Ausente = etiqueta impressa por build anterior ao #996 (ou nunca impressa).
 *
 * Usado por: `LabelSchema.printLocation`/`.nfcLocation` (`schemas.ts`);
 * `BindNfcTagInputSchema.location` (`nfc.ts`); callable `bindNfcTag` e trigger
 * `onLabelWritten` (agregado), em `apps/functions`; admin
 * `TenantLocationTab` (aba Local); Android `FirestoreLabelRepository.markPrinted`
 * e `FirebaseNfcTagRepository.bind` (espelho manual); iOS `LabelPayload.printMark`
 * e `FirebaseNfcTagRepository.bind` (espelho manual).
 *
 * Arquitetura: a posição é dado de observabilidade — nenhuma regra de negócio
 * (validade, status, saldo) lê estes campos. `fixedAtMillis` vem do provedor de
 * localização (relógio do GNSS/rede), nunca do relógio do sistema; o horário
 * autoritativo da agregação é o `serverTimestamp` do agregado.
 *
 * Dicas: o cliente bloqueia impressão e NFC sem posição (#996), mas as rules e
 * o callable aceitam etiqueta SEM ela, porque builds antigos gravam offline e a
 * recusa só apareceria na sincronização, com o papel já impresso. A regra passa
 * a valer para todos com a subida do `minBuild`.
 *
 * Issues: #996 (origem), #900 (NFC), #106 (iOS).
 */

/** De onde veio a posição. Ver o cabeçalho do arquivo. */
export const DeviceLocationSourceSchema = z.enum(['FRESH', 'LAST_KNOWN']);
export type DeviceLocationSource = z.infer<typeof DeviceLocationSourceSchema>;
export const DeviceLocationSource = DeviceLocationSourceSchema.enum;

/** Ação que capturou a posição: impressão (cliente) ou gravação NFC (servidor). */
export const DeviceLocationViaSchema = z.enum(['PRINT', 'NFC']);
export type DeviceLocationVia = z.infer<typeof DeviceLocationViaSchema>;
export const DeviceLocationVia = DeviceLocationViaSchema.enum;

/** Plataforma do aparelho que capturou a posição. */
export const DevicePlatformSchema = z.enum(['ANDROID', 'IOS']);
export type DevicePlatform = z.infer<typeof DevicePlatformSchema>;
export const DevicePlatform = DevicePlatformSchema.enum;

/**
 * Teto da precisão aceita, em metros. A posição aproximada do Android chega a
 * ~2 km e a "Localização precisa" desligada do iOS a ~3 km (owner aceitou a
 * aproximada, #996); 50 km folga para fix de rede ruim sem aceitar lixo.
 * Espelhado em `isValidDeviceLocation` das rules — mudar nos dois.
 */
export const DEVICE_LOCATION_MAX_ACCURACY_M = 50_000;

/**
 * Idade máxima da última posição conhecida aceita quando não há fix novo
 * (owner, #996: "usar última posição até 24 h"). Os clientes espelham à mão.
 */
export const DEVICE_LOCATION_MAX_LAST_KNOWN_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Posição do aparelho (ver o cabeçalho do arquivo).
 *
 * Origem: #996 — 2026-10-18 14:17 +0200.
 *
 * Usado por: `LabelSchema.printLocation`/`.nfcLocation`;
 * `BindNfcTagInputSchema.location`; trigger `onLabelWritten` (agregado).
 */
export const LabelDeviceLocationSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().positive().max(DEVICE_LOCATION_MAX_ACCURACY_M),
  fixedAtMillis: z.number().int().nonnegative(),
  source: DeviceLocationSourceSchema,
  capturedVia: DeviceLocationViaSchema,
  platform: DevicePlatformSchema,
});
export type LabelDeviceLocation = z.infer<typeof LabelDeviceLocationSchema>;

/**
 * Posição como o cliente manda no `bindNfcTag` — sem `capturedVia`, que o
 * servidor fixa em `NFC` (o cliente não escolhe a via).
 */
export const ClientDeviceLocationSchema = LabelDeviceLocationSchema.omit({ capturedVia: true });
export type ClientDeviceLocation = z.infer<typeof ClientDeviceLocationSchema>;

/**
 * Precisão do geohash da célula do agregado: 6 caracteres ≈ 1,2 km × 0,6 km.
 * Casa com a posição aproximada aceita (#996) — célula menor espalharia o
 * mesmo restaurante em várias linhas quando a precisão é de km.
 */
export const OPERATING_LOCATION_GEOHASH_PRECISION = 6;

const GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Geohash padrão (base32, bits intercalados lng/lat) de uma coordenada.
 *
 * Origem: #996 — 2026-10-18 14:17 +0200. Implementação local para não trazer
 * dependência só por 20 linhas (o mesmo hash é chave de doc no servidor e
 * rótulo no admin).
 */
export function encodeGeohash(lat: number, lng: number, precision: number): string {
  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;
  let hash = '';
  let bits = 0;
  let bitCount = 0;
  let evenBit = true;
  while (hash.length < precision) {
    if (evenBit) {
      const mid = (lngMin + lngMax) / 2;
      if (lng >= mid) {
        bits = bits * 2 + 1;
        lngMin = mid;
      } else {
        bits *= 2;
        lngMax = mid;
      }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat >= mid) {
        bits = bits * 2 + 1;
        latMin = mid;
      } else {
        bits *= 2;
        latMax = mid;
      }
    }
    evenBit = !evenBit;
    bitCount += 1;
    if (bitCount === 5) {
      hash += GEOHASH_BASE32.charAt(bits);
      bits = 0;
      bitCount = 0;
    }
  }
  return hash;
}

/**
 * Célula do agregado "onde o tenant opera" —
 * `tenants/{tenantId}/operating_locations/{geohash6}`.
 *
 * Origem: #996 — 2026-10-18 14:17 +0200.
 *
 * Dados — escrito SÓ pelo trigger `onLabelWritten` (Admin SDK); lido SÓ por
 * super-admin (aba Local do admin). Exemplo anonimizado:
 *   {
 *     "geohash": "6gyf4b",
 *     "lat": -23.5613, "lng": -46.6565, // da MELHOR precisão vista na célula
 *     "bestAccuracyM": 12,
 *     "firstSeenAt": Timestamp,         // serverTimestamp
 *     "lastSeenAt": Timestamp,          // serverTimestamp da última sessão
 *     "sessions": 41,                   // sessões, não etiquetas — ver Dicas
 *     "lastVia": "PRINT",
 *     "lastPlatform": "ANDROID",
 *     "lastDeviceUid": "uid-1"          // quem imprimiu por último (statusChangedByUid)
 *   }
 *
 * Dicas: o trigger só reescreve a célula se `lastSeenAt` tem mais de 15 min ou
 * se a precisão melhorou — um lote de 100 etiquetas vira UMA escrita, e não
 * 100 incrementos no mesmo doc (limite de ~1 escrita/s por documento). Por isso
 * o contador é de sessões.
 *
 * Usado por: trigger `onLabelWritten` (`apps/functions`); admin
 * `useOperatingLocations` / `TenantLocationTab`.
 */
export const OperatingLocationSchema = z.object({
  geohash: z.string().min(1),
  lat: z.number(),
  lng: z.number(),
  bestAccuracyM: z.number().positive(),
  sessions: z.number().int().nonnegative(),
  lastVia: DeviceLocationViaSchema,
  lastPlatform: DevicePlatformSchema,
  lastDeviceUid: z.string().nullable().default(null),
});
export type OperatingLocation = z.infer<typeof OperatingLocationSchema>;
